import {Button, Combobox} from '@mira/ui';
import {Box, Center, Heading, VStack} from 'platform/foundation-tw';

import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {test} from 'ramda';

import {useNavigate} from 'shared/web';

import {vinRegex} from 'features/vin-validate';

const VIN_LENGTH = 17;

/**
 * Demo VINs are only meant for internal sandboxing — never ship to end users.
 * Gated by env so production builds get an empty combobox while dev/staging
 * keep the convenience suggestions.
 */
const HARDCODED_VINS = [
  'TMBJG7NE3F0165840',
  'TMBJF73T9C9021592',
  'TMBLK9NS7J8059720',
  'TMBCJ61Z4B2119946',
  'TMBJP6NJ0PZ011221',
  'TMBLJ9NS2L8028445',
  'TMBJJ9NX5LY008466',
  'TMBAN7NP0M7043805',
  'TMBAR7NP1N7053989',
  'TMBJH7NP9N7022363',
  'TMBUF61Z3C2160978',
  'TMBLJ7NU9K5030098',
  'TMBJN6NJ0GZ015114',
  'TMBJJ7NS8K8080429',
  'TMBJJ7NX1MY113284',
] as const;

const SHOW_DEMO_VINS =
  process.env.NODE_ENV === 'development' || process.env.MIRA_SHOW_DEMO_VINS === 'true';

const VIN_OPTIONS = SHOW_DEMO_VINS ? HARDCODED_VINS.map((vin) => ({value: vin, label: vin})) : [];

export function VinInput() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [vin, setVin] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const handleVinChange = (value: string) => {
    setVin(value.toUpperCase());
    setError(undefined);
  };

  const handleSubmit = () => {
    const trimmedVin = vin.trim();

    if (trimmedVin.length === 0) {
      setError(t('vinInput.error.required'));
      return;
    }

    if (trimmedVin.length !== VIN_LENGTH) {
      setError(t('vinInput.error.length', {length: VIN_LENGTH}));
      return;
    }

    if (!test(vinRegex, trimmedVin)) {
      setError(t('vinInput.error.format'));
      return;
    }

    navigate(`/report/${trimmedVin}`);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Box minHeight="100dvh" padding={8}>
      <Center height="100%">
        <Box maxWidth="30rem" width="100%">
          <VStack spacing={6}>
            <Heading size={1}>{t('vinInput.heading')}</Heading>
            <div onKeyDown={handleKeyDown}>
              <VStack spacing={4}>
                <Combobox
                  label={t('vinInput.label')}
                  value={vin}
                  onChange={handleVinChange}
                  options={VIN_OPTIONS}
                  error={error}
                  placeholder={t('vinInput.placeholder')}
                  name="vin"
                />
                <Button type="button" onClick={handleSubmit}>
                  {t('vinInput.submit')}
                </Button>
              </VStack>
            </div>
          </VStack>
        </Box>
      </Center>
    </Box>
  );
}
